import { apiService, isImpersonating } from "api/apiService";
import { getRedirectPath } from "common/authenticationConstants";
import { clearAuthStorage } from "composables/useHelpers";
import { defineStore } from "pinia";
import { useQuasar } from "quasar";
import { useAuthenticationStore } from "store/modules/authenticationStore";

/**
 * Authorization Store
 *
 * This store manages the roles used in the application.
 */
export const useAuthorizationStore = defineStore("authorization", () => {
  // Quasar
  const $q = useQuasar();

  // Authenthication Store
  const authenticationStore = useAuthenticationStore();
  const { processResults } = authenticationStore;

  /**
   * The roles used in the application.
   * @type {Array<Object>}
   * @property {string} label - The display label of the role.
   * @property {string} name - The internal name of the role.
   */
  const allRoles = [
    {
      label: "Webmaster",
      name: "webmaster",
    },
    {
      name: "manager",
      label: "Applicatiebeheerder",
    },
    {
      name: "organizer",
      label: "Organisator",
    },
    {
      name: "teamleader",
      label: "Teamleider",
    },
    {
      name: "decaan",
      label: "Organisator portfolio",
    },
    {
      name: "teacher",
      label: "Docent",
    },
    {
      name: "student",
      label: "Student",
    },
  ];

  const impersonateUser = async ({
    userId = null,
    impersonatedUserType = null,
    redirect = null,
  } = {}) => {
    if (!userId) {
      $q.notify({
        message: "Geen employeeID meegestuurd.",
        type: "negative",
      });
      return;
    }

    try {
      const { data } = await apiService.post(`/api/impersonate/${userId}`);

      // Store impersonated user type for redirect on return
      localStorage.setItem("impersonated_user_type", impersonatedUserType);

      $q.notify({
        message:
          "Je wordt nu ingelogd als een andere gebruiker, de pagina wordt opnieuw geladen.",
        type: "positive",
      });

      await processResults(data, redirect);

      return data;
    } catch {
      $q.notify({
        message: "Er is iets fout gegaan bij het inloggen.",
        type: "negative",
      });
    }
  };

  const returnAsWebmaster = async () => {
    try {
      const impersonatedUserType = localStorage.getItem(
        "impersonated_user_type",
      );

      // Backend uses session to know who the original webmaster is
      const { data } = await apiService.post("/api/impersonate/return");

      // Clean up impersonation-related localStorage items
      localStorage.removeItem("impersonated_user_type");

      const redirectPath = getRedirectPath(impersonatedUserType);

      await processResults(data, redirectPath);

      return data;
    } catch (error) {
      console.log(error);
      $q.notify({
        message:
          "Er is iets fout gegaan bij het inloggen, je moet opnieuw inloggen. De pagina wordt opnieuw geladen.",
        type: "negative",
      });

      setTimeout(() => {
        clearAuthStorage();
        window.location.reload();
      }, 2000);
    }
  };

  return {
    allRoles,
    isImpersonating,
    impersonateUser,
    returnAsWebmaster,
  };
});
